/**
 * The two durable pieces of the session, the one non-secret hint that outlives them, and the
 * two per-tab pieces added by D152.
 *
 * `device-cache.md` → *"Refresh token and server-issued session binding"*: origin-scoped
 * IndexedDB, cleared on **sign-out, current-session revoke, LRU eviction once detected,
 * administrative removal, token-family reuse detection, or terminal refresh failure**. All
 * six arrive at `clearCredentials()` — there is no other writer of that store.
 *
 * **D339: in the store-delivered container that row is not in IndexedDB at all.** The three
 * functions below keep their signatures and route through `credentialStoreFor(store)`, which hands
 * back `nativeCredentials.ts`'s keystore adapter inside the container and `store` itself everywhere
 * else — so the browser PWA reads and writes exactly what it did, and the six clear paths still reach
 * the one function. D152's refresh marker below deliberately stays on `store`: it is a diagnostic,
 * not the credential.
 *
 * **Nothing here ever goes to `localStorage`, a URL, a log, analytics, or the service
 * worker's cache**, with two deliberate exceptions described below that carry no secret — the
 * resume hint, and the pending-clear flag a failed sign-out leaves.
 *
 * **D152 adds two more, deliberately not durable across a browser restart.** The access
 * token lives in `sessionStorage` — per-tab, gone when the tab closes, never written to
 * IndexedDB or `localStorage` — so a same-tab reload with time left on the token can skip
 * `/auth/refresh` entirely instead of rotating the refresh family on every load. The refresh
 * marker is the opposite: it *is* durable (same IndexedDB database, same
 * `transaction.oncomplete` guarantee as the credentials row), because its whole job is to
 * survive the one thing `sessionStorage` cannot — a reload's fresh document reading it back.
 */

import type { KeyValueStore } from '../device-cache/store'
import { credentialStoreFor } from './nativeCredentials'

export interface StoredCredentials {
  readonly userId: string
  readonly sessionId: string
  /**
   * The binding token, returned exactly once by `POST /auth/otp/verify` and stored here
   * because a re-issue is impossible without another OTP. It is the credential; it is never
   * rendered, logged, or put in a URL.
   */
  readonly binding: string
  readonly refreshToken: string
  /** Rotated by every refresh. A stale value is what P2 treats as a replayed token. */
  readonly refreshFamilyId: string
}

/**
 * One signed-in account per device per stage, which is what the product's account switch
 * means: signing in as someone else overwrites this row, and the previous account's snapshot
 * is purged in the same step (`purge.ts`).
 */
const credentialsKey = (stage: string): string => stage

export async function readCredentials(
  store: KeyValueStore,
  stage: string,
): Promise<StoredCredentials | undefined> {
  const target = credentialStoreFor(store)
  // A session end whose clear failed is finished here, before anything reads the row it meant to
  // remove. A remove that fails again rejects, and the restore loop retries it — never a read of
  // the row as if the sign-out had not happened.
  if (clearPending(stage)) {
    await target.remove('credentials', credentialsKey(stage))
    forgetClearPending(stage)
    return undefined
  }
  const record = await target.get<StoredCredentials>('credentials', credentialsKey(stage))
  if (record === undefined) return undefined
  // A half-written row is treated as no row. Every field is load-bearing: without any one of
  // them the next request is a 401 that the client cannot recover from, so the honest
  // outcome is the OTP screen rather than a session that fails on its first use.
  if (
    typeof record.userId !== 'string' ||
    typeof record.sessionId !== 'string' ||
    typeof record.binding !== 'string' ||
    typeof record.refreshToken !== 'string' ||
    typeof record.refreshFamilyId !== 'string'
  ) {
    await target.remove('credentials', credentialsKey(stage))
    return undefined
  }
  return record
}

export async function writeCredentials(
  store: KeyValueStore,
  stage: string,
  credentials: StoredCredentials,
): Promise<void> {
  await credentialStoreFor(store).put('credentials', credentialsKey(stage), credentials)
  // A new session supersedes a sign-out still waiting to be finished.
  forgetClearPending(stage)
}

/**
 * **A clear that fails is recorded, so the sign-out still holds on the next launch.** Every caller
 * that survives a failed clear — `endSession`'s fallback, `purgeEverything` — goes on to destroy
 * IndexedDB, which takes the browser's row with it but never reaches the container's keystore
 * (D339). Without the record, the next launch reads the adopted keystore row and signs the previous
 * account back in on a phone the resident believes they signed out of.
 */
export async function clearCredentials(store: KeyValueStore, stage: string): Promise<void> {
  try {
    await credentialStoreFor(store).remove('credentials', credentialsKey(stage))
  } catch (failure) {
    markClearPending(stage)
    throw failure
  }
  forgetClearPending(stage)
}

/*
 * ---------------------------------------------------------------------------
 * The pending clear — a sign-out whose credential clear failed.
 * ---------------------------------------------------------------------------
 *
 * 🔸 A second `localStorage` key beside the resume hint, on the same argument: it carries **no
 * secret** — a flag, no id, no token — and it must survive whatever made the clear fail, which a
 * row in the store that just refused is the one place it cannot. Every access is guarded; an
 * unreadable `localStorage` answers *not pending*, which is exactly the behaviour before this
 * existed, so it can only narrow the failure and never add one. The stated cost: if the removal in
 * `writeCredentials` fails while an older mark stands, the next launch clears the new session too —
 * one OTP, and fail-closed in the direction D128 already takes.
 */

const clearPendingKey = (stage: string): string => `selogate.${stage}.credentials-clear-pending`

function clearPending(stage: string): boolean {
  try {
    return globalThis.localStorage?.getItem(clearPendingKey(stage)) === '1'
  } catch {
    return false
  }
}

function markClearPending(stage: string): void {
  try {
    globalThis.localStorage?.setItem(clearPendingKey(stage), '1')
  } catch {
    /* ignore — see above */
  }
}

function forgetClearPending(stage: string): void {
  try {
    globalThis.localStorage?.removeItem(clearPendingKey(stage))
  } catch {
    /* ignore — see above */
  }
}

/*
 * ---------------------------------------------------------------------------
 * The resume hint — §13 "reuses its session record rather than consuming an additional slot"'s "reuses its session record rather than consuming an
 * additional slot".
 * ---------------------------------------------------------------------------
 *
 * P2 accepts `resumeSessionId` on `POST /auth/otp/verify` for *"the id an iOS client
 * remembers after Safari evicted its PWA storage"*. For the client to remember it, it has to
 * survive whatever removed the credentials — so keeping it **in the same IndexedDB row that
 * was just evicted** would make the parameter permanently dead code.
 *
 * 🔸 So the hint lives in `localStorage`, separately, and holds **only the session id**: no
 * binding, no token, no phone number. That is safe to keep where the secrets may not go
 * because `binding.ts` states what a session id is worth on its own — *"a lookup hint and
 * never authorization evidence"* — and `createSession()` honours a resume id only when the
 * record it names carries the **same Cognito `sub`** the caller just authenticated as. A
 * stolen, stale, or foreign hint therefore buys nothing: it is silently ignored and the
 * login proceeds as new.
 *
 * It does not survive a storage clear that takes everything (iOS's 7-day cap does), and it
 * is not meant to: what it covers is the far more common partial loss — an IndexedDB quota
 * eviction, a corrupted database, or a schema discard — where the alternative is a user
 * quietly burning one of their three device slots on every re-auth.
 */

const resumeKey = (stage: string): string => `selogate.${stage}.resume-session`

export function readResumeHint(stage: string): string | undefined {
  try {
    return globalThis.localStorage?.getItem(resumeKey(stage)) ?? undefined
  } catch {
    // Private mode, or storage disabled. The parameter is an optimization; its absence
    // costs a session slot, never a sign-in.
    return undefined
  }
}

export function writeResumeHint(stage: string, sessionId: string): void {
  try {
    globalThis.localStorage?.setItem(resumeKey(stage), sessionId)
  } catch {
    /* ignore — see readResumeHint */
  }
}

export function clearResumeHint(stage: string): void {
  try {
    globalThis.localStorage?.removeItem(resumeKey(stage))
  } catch {
    /* ignore — see readResumeHint */
  }
}

/*
 * ---------------------------------------------------------------------------
 * The access token — D152, per tab, in `sessionStorage`.
 * ---------------------------------------------------------------------------
 *
 * Before D152 the access token lived only in a `SessionManager` field, which is why a reload
 * always began with a refresh: there was nothing else to adopt. D152 promotes the 🔸 assumption
 * in `device-cache.md` that kept it memory-only, on the record that a token readable for the
 * tab's *lifetime* (until the tab closes) rather than the *page's* (until the next reload) is a
 * near-identical exposure window for a same-tab SPA, paid for a real defect: repeated Refresh no
 * longer rotates the refresh family, so it no longer trips the server's single-use replay
 * detection.
 *
 * It is never the binding, never the refresh token, and never written anywhere durable — a tab
 * close loses it exactly as a memory field would, and it is scoped per stage exactly like the
 * credentials row, so switching stages in dev tooling cannot restore the wrong environment's
 * token.
 */

export interface StoredAccessToken {
  readonly token: string
  /** `Date.now()`-comparable, already carrying `ACCESS_TOKEN_SKEW_SECONDS`. */
  readonly expiresAt: number
}

const accessTokenKey = (stage: string): string => `selogate.${stage}.access-token`

/**
 * `sessionStorage.getItem`/`setItem` throw in Safari private mode, and a value written by a
 * different build can be malformed. Either one degrades to "no cached token" — today's
 * behavior, a refresh on this load — never to a session ending.
 */
export function readAccessToken(stage: string): StoredAccessToken | undefined {
  try {
    const raw = globalThis.sessionStorage?.getItem(accessTokenKey(stage))
    if (raw === null || raw === undefined) return undefined
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof (parsed as Partial<StoredAccessToken>).token !== 'string' ||
      typeof (parsed as Partial<StoredAccessToken>).expiresAt !== 'number'
    ) {
      return undefined
    }
    return parsed as StoredAccessToken
  } catch {
    return undefined
  }
}

/** Best-effort. A failed write costs this tab one extra refresh on its next reload, not a session. */
export function writeAccessToken(stage: string, record: StoredAccessToken): void {
  try {
    globalThis.sessionStorage?.setItem(accessTokenKey(stage), JSON.stringify(record))
  } catch {
    /* ignore — see readAccessToken */
  }
}

export function clearAccessToken(stage: string): void {
  try {
    globalThis.sessionStorage?.removeItem(accessTokenKey(stage))
  } catch {
    /* ignore — see readAccessToken */
  }
}

/*
 * ---------------------------------------------------------------------------
 * The refresh marker — D152, durable, for the window `sessionStorage` cannot cover.
 * ---------------------------------------------------------------------------
 *
 * A same-tab reload that lands *during* an in-flight `/auth/refresh` tears down the document
 * before it can run `writeCredentials(rotated)` — even though the server may already have
 * committed the rotation. The rotated tokens themselves are unrecoverable: they arrive in a
 * response body the reload discards. This marker cannot fix that; it names the family id about
 * to be presented, durably, before the request goes out, and is cleared only once the rotation
 * it produced is itself durable — so a later read can tell whether the id it is about to present
 * was already mid-flight once. `SessionManager` uses that to make a resulting refusal an honest,
 * non-thrashing one; it never suppresses a real `401`.
 */

export interface RefreshMarker {
  readonly familyId: string
}

const refreshMarkerKey = (stage: string): string => `refreshing:${stage}`

export async function readRefreshMarker(
  store: KeyValueStore,
  stage: string,
): Promise<RefreshMarker | undefined> {
  const record = await store.get<RefreshMarker>('credentials', refreshMarkerKey(stage))
  if (record === undefined || typeof record.familyId !== 'string') return undefined
  return record
}

export async function writeRefreshMarker(
  store: KeyValueStore,
  stage: string,
  familyId: string,
): Promise<void> {
  await store.put('credentials', refreshMarkerKey(stage), { familyId })
}

export async function clearRefreshMarker(store: KeyValueStore, stage: string): Promise<void> {
  await store.remove('credentials', refreshMarkerKey(stage))
}
